import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import { ROUTES } from 'src/constants';
import {
  dismissShareSetUpdates,
  unsubscribeFromShareSetUpdates,
} from 'src/actions/accountActions';

// CON-1552 Chunk 6 Stage C — the recipient sign-in notice that one or more
// Sets they applied have newer published versions. Read-only prompt: "Review
// updates" routes to the Share Center (where Stage D's update picker lives);
// "Not now" stamps the dedup so they are not re-prompted until the next
// publish; each row's "Stop notifying me" is the per-Set permanent opt-out.
// Third parallel rider on the existing sign-in modal mechanism (peer of the
// What's New / PB Texting modals), mounted in DashboardLayout.
function ShareSetUpdatesModal() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { shouldShowModal, sets = [] } = useSelector(
    (state) => state.account?.shareSetUpdates,
  ) || {};

  // Local copy so "Stop notifying me" can drop a row without a re-fetch.
  const [rows, setRows] = useState(sets);
  useEffect(() => {
    setRows(sets);
  }, [sets]);

  // Echo back exactly what was shown so the dedup stamps the version the
  // recipient actually saw.
  const seenPayload = useCallback(
    () => rows.map((s) => ({ shareSetId: s.shareSetId, latestVersionId: s.latestVersionId })),
    [rows],
  );

  const handleNotNow = useCallback(() => {
    dispatch(dismissShareSetUpdates(seenPayload()));
  }, [dispatch, seenPayload]);

  const handleReview = useCallback(() => {
    dispatch(dismissShareSetUpdates(seenPayload()));
    // Deep-link straight to the picker when exactly ONE Share Set has updates;
    // otherwise land on the Share Center list so the recipient can choose.
    if (rows.length === 1) {
      history.push(`${ROUTES.APP_SHARE_CENTER}?reviewSet=${rows[0].shareSetId}`);
    } else {
      history.push(ROUTES.APP_SHARE_CENTER);
    }
  }, [dispatch, seenPayload, history, rows]);

  const handleStopNotifying = useCallback(
    (shareSetId) => {
      dispatch(unsubscribeFromShareSetUpdates(shareSetId));
      setRows((prev) => {
        const next = prev.filter((s) => s.shareSetId !== shareSetId);
        // Nothing left to show -> close (and stamp the rest, which is none).
        if (next.length === 0) {
          dispatch(dismissShareSetUpdates([]));
        }
        return next;
      });
    },
    [dispatch],
  );

  if (!shouldShowModal || rows.length === 0) {
    return null;
  }

  const content = (
    <Box>
      <Typography variant="body1" sx={{ mb: 0.5 }}>
        One or more of your Share Sets has updates available from its author.
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        A Share Set is a bundle of items another person shared with you. When
        they change it, you choose which updates to apply to your own copies.
      </Typography>
      {rows.map((s) => (
        <Box
          key={s.shareSetId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
              {s.shareSetName}
            </Typography>
            {s.authorName && (
              <Typography variant="caption" color="textSecondary" component="div">
                {`Shared by ${s.authorName}`}
              </Typography>
            )}
            {(s.appliedVersionNumber != null && s.latestPublishedVersionNumber != null) && (
              <Typography variant="caption" color="textSecondary" component="div">
                {`Applied v${s.appliedVersionNumber} · latest v${s.latestPublishedVersionNumber}`}
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            color="inherit"
            onClick={() => handleStopNotifying(s.shareSetId)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Stop notifying me
          </Button>
        </Box>
      ))}
    </Box>
  );

  return (
    <ModalDialogMUI6
      isVisible
      isHeaderVisible
      title="Share Set updates available"
      content={content}
      onClose={handleNotNow}
      closeText="Not now"
      onOk={handleReview}
      okText="Review updates"
      modalSize="sm"
    />
  );
}

export default ShareSetUpdatesModal;
